import { addCrc, concatTypedArray, crc16 } from './CommonUtil'
// import { log, toHexString } from './LogUtil'
import { MODE_LIST_IMAGES } from '../NordicUpdater'
import TaskExecutor from './TaskExecutor'

const smp_first_header = new Uint8Array([0x06, 0x09])
const smp_continuation_header = new Uint8Array([0x04, 0x14])
const NORDIC_MAX_PACKET_SIZE = 32

export class HidSender {
  device: HIDDevice
  sendIndex = 0
  mode: number | null = null

  constructor(device: HIDDevice) {
    this.device = device // HIDDevice对象
  }

  async send(message: Uint8Array | number[], mode: number | null = null): Promise<void> {
    this.mode = mode
    const data = new Uint8Array(message)
    let output: Uint8Array = new Uint8Array()

    const size = data.length + 2
    // log.info(`size:${size}`)
    const sizeBytes = new Uint8Array([
      (size >> 8) & 0xff, // 高位
      size & 0xff, // 低位
    ])

    output = concatTypedArray(output, sizeBytes)

    const crc = crc16(data, 0x1021, 0, true)

    let header = new Uint8Array(smp_first_header)
    let pos = 0

    // log.logHexStr(data)
    while (pos < data.length + 1) {
      // +1处理边界
      let chunkSize = 93 - output.length
      // log.info("pos: " + pos + ", chunkSize: " + chunkSize)
      // 分块调整
      if (chunkSize + pos > data.length) {
        chunkSize = data.length - pos
        // log.info("last pos: " + pos + ", chunkSize: " + chunkSize)
        if (chunkSize === 0) {
          output = addCrc(output, crc)
          await this._sendChunkWithIndex(header, output)

          break
        }
      }

      // 追加数据块
      const chunk = data.subarray(pos, pos + chunkSize)

      output = concatTypedArray(output, chunk)

      pos += chunkSize

      // 最后一个块处理
      if (pos === data.length && 93 - output.length >= 2) {
        output = addCrc(output, crc)
        pos += 2 // 仅用于循环控制
      }

      await this._sendChunkWithIndex(header, output)

      // 重置为后续头
      header = new Uint8Array(smp_continuation_header)
      output = new Uint8Array()
    }
  }

  async _sendChunkWithIndex(header: Uint8Array, payload: Uint8Array): Promise<void> {
    // 构造完整数据块: header + base64(payload) + 换行
    const base64Str = btoa(String.fromCharCode(...payload))
    const fullData = concatTypedArray(header, new TextEncoder().encode(base64Str + '\n'))

    const lenth = fullData.length

    const reportId = 0x09

    if (this.mode === MODE_LIST_IMAGES) {
      this.sendIndex = 0
    }

    const packetSize = NORDIC_MAX_PACKET_SIZE - 2
    const packetNum = Math.floor(lenth / packetSize)
    const packetRemain = lenth % packetSize
    for (let i = 0; i < packetNum; i++) {
      const dataTemp = fullData.slice(i * packetSize, (i + 1) * packetSize)

      const writeBuf = new Uint8Array(NORDIC_MAX_PACKET_SIZE + 1)
      writeBuf[0] = packetSize + 1
      writeBuf[1] = this.sendIndex % 0xff
      writeBuf.set(dataTemp, 2)

      // log.info(`writeBuf:${toHexString(writeBuf, '')}`)
      await this.sendData(reportId, writeBuf)
    }
    if (packetRemain) {
      const dataTemp = fullData.slice(-packetRemain)
      const writeBuf = new Uint8Array(NORDIC_MAX_PACKET_SIZE + 1)
      writeBuf[0] = packetRemain + 1
      writeBuf[1] = this.sendIndex % 0xff
      writeBuf.set(dataTemp, 2)

      // log.info(`writeBuf:${toHexString(writeBuf, '')}`)
      await this.sendData(reportId, writeBuf)
    }
  }

  async sendData(reportId: number, sendBuffer: Uint8Array): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // 将任务包装并加入队列
      const task = async (): Promise<void> => {
        try {
          await this.sendSmpOrRetryTask(reportId, sendBuffer)
          resolve()
        } catch (error) {
          reject(error)
          throw error // 保持队列继续处理后续任务
        }
      }
      TaskExecutor.getInstance().executeTask(task)
    })
  }

  async sendSmpOrRetryTask(reportId: number, sendBuffer: Uint8Array): Promise<void> {
    // log.info(`sendSmpOrRetry start :${Date.now()}`)
    const maxRetries = 4
    let retries = 0

    while (retries < maxRetries) {
      try {
        const ack = await this.sendSmpAndRetry(reportId, sendBuffer)
        if (ack === 0x01) {
          //发送成功
          break
        }
        retries++
      } catch (error) {
        console.error('重新发送报错:', error)
        retries++
        const ack = await this.sendSmpAndRetry(reportId, sendBuffer)
        if (ack === 0x01) {
          //发送成功
          break
        }
      }
    }
    if (retries >= maxRetries) {
      throw new Error('达到最大重试次数，获取数据失败')
    }
    this.sendIndex += 1
    await new Promise((resolve) => setTimeout(resolve, 2))
  }

  async sendSmpAndRetry(reportId: number, sendData: Uint8Array): Promise<number> {
    try {
      if (!this.device || !this.device.opened) throw new Error('Device not opened')
      await this.device.sendFeatureReport(reportId, new Uint8Array(sendData))
    } catch (error) {
      console.error('WebHID send error:', error)
      throw error
    }
    //TODO 延迟3毫秒，获取结果
    await new Promise((resolve) => setTimeout(resolve, 3))
    const maxRetries = 4
    const timeoutMs = 3000
    let retries = 0
    const startTime = Date.now()

    while (retries < maxRetries) {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('获取数据超时')
      }

      const hexData = await this.getFeatureReport(0x09)
      // log.info(`hexData:${toHexString(new Uint8Array(hexData.buffer), ',0x')}`)
      const ack = hexData.getUint8(3)

      if (ack !== 0x01) {
        //繁忙或者失败
        console.log(`鼠标正处理中，第 ${retries + 1} 次重试，等待3ms,ack:${ack}`)
        await new Promise((resolve) => setTimeout(resolve, 3))
        retries++
        continue
      }
      // console.log(`鼠标处理成功 ${this.sendIndex}`)
      return ack
    }
    throw new Error('达到最大重试次数，获取数据失败')
  }

  async sendAndGetFeatureDataTask(reportId: number, sendData: Uint8Array): Promise<DataView> {
    try {
      if (!this.device || !this.device.opened) throw new Error('Device not opened')
      await this.device.sendReport(reportId, new Uint8Array(sendData))
    } catch (error) {
      console.error('WebHID send error:', error)
      throw error
    }

    const maxRetries = 5
    const timeoutMs = 3000
    let retries = 0
    const startTime = Date.now()

    while (retries < maxRetries) {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('获取数据超时')
      }

      try {
        const hexData = await this.getFeatureReport(0x08)
        const ack = hexData.getUint8(0)
        if (ack === 0x02) {
          console.log(`处理中，第 ${retries + 1} 次重试，等待100ms`)
          await new Promise((resolve) => setTimeout(resolve, 100))
          retries++
          continue
        }
        console.log('WebHID get data:', hexData)
        return hexData
      } catch (error) {
        console.error('WebHID get data error:', error)
        if (retries === maxRetries - 1) {
          throw error
        }
        retries++
      }
    }
    throw new Error('达到最大重试次数，获取数据失败')
  }

  async sendAndGetFeatureData(
    reportId: number,
    sendData: Uint8Array,
  ): Promise<DataView | undefined> {
    return new Promise<DataView | undefined>((resolve, reject) => {
      // 将任务包装并加入队列
      const task = async (): Promise<void> => {
        try {
          const result = await this.sendAndGetFeatureDataTask(reportId, sendData)
          resolve(result)
        } catch (error) {
          reject(error)
          throw error // 保持队列继续处理后续任务
        }
      }
      TaskExecutor.getInstance().executeTask(task)
    })
  }

  async getFeatureReport(reportId: number): Promise<DataView> {
    if (!this.device || !this.device.opened) throw new Error('Device not opened')
    return await this.device.receiveFeatureReport(reportId)
  }
}
